import { AUTH_SESSION_STORAGE_KEY } from '@/constants/config'
import { normalizeApiPermission } from '@/constants/permissions'
import {
  apiClient,
  getStoredSession,
  parseStoredSession,
  setStoredSession,
  setTokenRefresher,
} from '@/gateway/client'
import { endpoints } from '@/gateway/endpoints'
import { ApiError } from '@/gateway/errors'
import type {
  AuthSessionResponse,
  AuthUserResponse,
  LoginMfaChallengeResponse,
  LoginRequest,
  LoginResponse,
  RefreshRequest,
  ResendLoginOtpRequest,
  VerifyLoginOtpRequest,
} from '@/types/api/auth'
import type {
  AuthSession,
  AuthUser,
  LoginCredentials,
  LoginMfaChallenge,
  LoginOutcome,
} from '@/types/domain/auth'
import { reportError } from '@/utils/reportError'

/**
 * Auth requests. The API issues an access token plus a refresh token and has no
 * `GET /auth/me` — but a reload does NOT call refresh to learn who the user is:
 * the stored session carries a user snapshot (see the session note in
 * `gateway/client.ts`), so the refresh endpoint is only called when the token
 * pair is actually near expiry. It matters because the endpoint is rate limited
 * (ten calls a minute) and a refresh-per-reload used to burn through that.
 */

/* ------------------------------------------------------------------ mapping -- */
function toAuthUser(user: AuthUserResponse): AuthUser {
  // The API grants keys in its own vocabulary (`payment.view`); the app checks
  // ours (`transactions:view`). Keep both forms so a check succeeds whichever
  // vocabulary the backend settles on — see `normalizeApiPermission`.
  const granted = user.permissions ?? []
  const permissions = Array.from(
    new Set(granted.flatMap((key) => [key, normalizeApiPermission(key)])),
  )

  return {
    id: user.id,
    email: user.email,
    userLogin: user.userLogin,
    fullName: user.fullName,
    roles: user.roles ?? [],
    permissions,
    isSuperadmin: user.isSuperadmin === true,
  }
}

/** Resolves the API's relative lifetimes into absolute expiry timestamps. */
function toSession(response: AuthSessionResponse): AuthSession {
  const now = Date.now()
  return {
    user: toAuthUser(response.user),
    tokens: {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      accessExpiresAt: now + response.expiresIn * 1000,
      refreshExpiresAt: now + response.refreshExpiresIn * 1000,
      accessLifetimeMs: response.expiresIn * 1000,
    },
  }
}

/** Resolves the challenge's relative lifetimes into absolute timestamps. */
function toMfaChallenge(response: LoginMfaChallengeResponse): LoginMfaChallenge {
  const now = Date.now()
  return {
    challengeId: response.challengeId,
    maskedEmail: response.maskedEmail,
    expiresAt: now + response.expiresIn * 1000,
    resendAvailableAt: now + response.resendAvailableInSeconds * 1000,
  }
}

function isMfaChallenge(response: LoginResponse): response is LoginMfaChallengeResponse {
  return 'mfaRequired' in response && response.mfaRequired === true
}

/* -------------------------------------------------------------------- login -- */
/**
 * Step one of sign-in. Since TURON-195 a correct password answers with an
 * emailed one-time code to verify (`verifyLoginOtp`), not with tokens; the
 * token branch is kept for a deployment with the second factor switched off.
 */
export async function login(credentials: LoginCredentials): Promise<LoginOutcome> {
  const response = await apiClient.post<LoginResponse>(
    endpoints.auth.login,
    { email: credentials.email, password: credentials.password } satisfies LoginRequest,
    { skipAuth: true },
  )

  if (isMfaChallenge(response)) {
    return { status: 'mfaRequired', challenge: toMfaChallenge(response) }
  }

  // The session (tokens + user snapshot) always goes to `localStorage` — there
  // is no per-login storage choice any more. See the note in `gateway/client.ts`.
  const session = toSession(response)
  setStoredSession(session)
  return { status: 'signedIn', session }
}

/**
 * Step two: the emailed code. A success returns exactly what login used to,
 * and is stored the same way. The code itself is never logged or kept.
 */
export async function verifyLoginOtp(request: VerifyLoginOtpRequest): Promise<AuthSession> {
  const response = await apiClient.post<AuthSessionResponse>(
    endpoints.auth.loginVerifyOtp,
    request,
    { skipAuth: true },
  )
  const session = toSession(response)
  setStoredSession(session)
  return session
}

/** A fresh code for the same sign-in; the returned challenge replaces the held one. */
export async function resendLoginOtp(request: ResendLoginOtpRequest): Promise<LoginMfaChallenge> {
  const response = await apiClient.post<LoginMfaChallengeResponse>(
    endpoints.auth.loginResendOtp,
    request,
    { skipAuth: true },
  )
  return toMfaChallenge(response)
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post<void>(endpoints.auth.logout)
  } finally {
    // Always drop the local session, even if the server call fails.
    setStoredSession(null)
  }
}

/* ------------------------------------------------------------------ refresh -- */
/**
 * One refresh at a time. The endpoint rotates the refresh token, so two
 * concurrent calls would send the same token twice and the second would be
 * rejected — logging the user out mid-session. Every caller (the API client
 * retrying a 401, and `AuthProvider` restoring on boot) shares this promise.
 */
let inFlightRefresh: Promise<RefreshOutcome> | null = null

/**
 * What a refresh attempt actually established. The three cases must stay
 * distinct: only `signedOut` means the user has to sign in again.
 *
 * ```text
 * renewed    a fresh pair is stored, `session` is the new session
 * signedOut  the server refused the refresh token — the session is over
 * unknown    no verdict (429 / 5xx / timeout / offline) — the stored refresh
 *            token is untouched and may well still be good
 * ```
 */
export type RefreshOutcome =
  | { readonly status: 'renewed'; readonly session: AuthSession }
  | { readonly status: 'signedOut' }
  | { readonly status: 'unknown'; readonly error: unknown }

/**
 * Exchanges the stored refresh token for a new session. Never rejects — the
 * outcome is reported in the resolved value so a caller cannot accidentally
 * read "we could not ask" as "the session is gone".
 */
export function refreshSession(): Promise<RefreshOutcome> {
  inFlightRefresh ??= refreshAcrossTabs().finally(() => {
    inFlightRefresh = null
  })
  return inFlightRefresh
}

/** Web Locks name shared by every tab of this origin. */
const REFRESH_LOCK = 'turon-admin.auth-refresh'

/** BroadcastChannel on which a tab announces the pair it has just rotated. */
const SESSION_CHANNEL = 'turon-admin.auth-session'

/**
 * How long the tab that rotated the pair keeps the lock after storing it.
 * `localStorage` writes reach other tabs' renderer processes asynchronously,
 * while the lock release travels a different path — so the next tab in line
 * could acquire the lock and still read the pair that was just spent (seen
 * 2026-10-14: a tab sent the superseded token in the same millisecond the
 * rotation response arrived in another). Holding the lock a moment lets the
 * write land first. Paid once per rotation, by one tab, and only on a renewal.
 */
const STORAGE_SETTLE_MS = 250

/**
 * How long a tab that waited for the lock gives the other tab's rotation to
 * become visible before concluding that nobody rotated anything.
 */
const ROTATION_VISIBILITY_TIMEOUT_MS = 1_500

/**
 * Announces rotated sessions to the other tabs. Its only job is to make the new
 * pair visible in every tab sooner, and more reliably, than the storage write
 * alone: a listener adopts it into this tab's own storage, which the rest of
 * this module then reads consistently.
 */
const sessionChannel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SESSION_CHANNEL)

sessionChannel?.addEventListener('message', (event: MessageEvent<unknown>) => {
  // Same-origin only, but still data: validate before storing.
  const announced = parseStoredSession(event.data)
  if (announced === null || announced.user === null) return

  // Only a tab still holding a session for this same user adopts the pair. A tab
  // that has signed out must not be signed back in by a late announcement.
  const current = getStoredSession()
  if (current?.user?.id !== announced.user.id) return

  const { user, ...tokens } = announced
  setStoredSession({ user, tokens })
})

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

/** The stored session, if it is no longer the one this tab set out to spend. */
function rotatedSince(tokenBefore: string | undefined): AuthSession | null {
  const current = getStoredSession()
  if (current === null || current.user === null || current.refreshToken === tokenBefore) return null

  const { user, ...tokens } = current
  return { user, tokens }
}

/**
 * Waits for another tab's rotation to show up in this tab's storage: a
 * `storage` event for the session key, the broadcast announcement, or the
 * timeout — whichever comes first — then reads again.
 */
async function awaitRotation(tokenBefore: string | undefined): Promise<AuthSession | null> {
  const already = rotatedSince(tokenBefore)
  if (already !== null) return already

  await new Promise<void>((resolve) => {
    const timeoutId = window.setTimeout(done, ROTATION_VISIBILITY_TIMEOUT_MS)

    function onStorage(event: StorageEvent): void {
      if (event.key === null || event.key === AUTH_SESSION_STORAGE_KEY) done()
    }

    function done(): void {
      window.clearTimeout(timeoutId)
      window.removeEventListener('storage', onStorage)
      sessionChannel?.removeEventListener('message', done)
      resolve()
    }

    window.addEventListener('storage', onStorage)
    sessionChannel?.addEventListener('message', done)
  })

  return rotatedSince(tokenBefore)
}

/** Refreshes while holding the lock, then lets the stored pair settle (see `STORAGE_SETTLE_MS`). */
async function refreshHoldingLock(): Promise<RefreshOutcome> {
  const outcome = await performRefresh()
  if (outcome.status === 'renewed') {
    sessionChannel?.postMessage({ ...outcome.session.tokens, user: outcome.session.user })
    await sleep(STORAGE_SETTLE_MS)
  }
  return outcome
}

/**
 * `inFlightRefresh` only de-duplicates within one tab. Every tab reads the same
 * stored pair, so two tabs refreshing together would present the same refresh
 * token twice — which the API reads as token theft and answers by revoking
 * every session the user has. The Web Lock serialises refreshes across tabs.
 *
 * Uncontended, the lock is taken at once and the refresh simply runs. When
 * another tab already holds it, this tab queues behind it and, once in, adopts
 * the pair that tab stored instead of spending it again. Reading storage the
 * instant the lock changes hands is not enough for that — the other tab's write
 * may not have reached this process yet — so the waiting tab gives the rotation
 * a moment to become visible (`awaitRotation`) and the rotating tab holds the
 * lock briefly after writing (`refreshHoldingLock`).
 */
async function refreshAcrossTabs(): Promise<RefreshOutcome> {
  if (typeof navigator === 'undefined' || !('locks' in navigator)) return performRefresh()

  const tokenBefore = getStoredSession()?.refreshToken

  const uncontended: RefreshOutcome | null = await navigator.locks.request(
    REFRESH_LOCK,
    { ifAvailable: true },
    (lock) => (lock === null ? Promise.resolve(null) : refreshHoldingLock()),
  )
  if (uncontended !== null) return uncontended

  return navigator.locks.request(REFRESH_LOCK, async () => {
    const adopted = await awaitRotation(tokenBefore)
    if (adopted !== null) return { status: 'renewed', session: adopted } satisfies RefreshOutcome
    return refreshHoldingLock()
  })
}

async function performRefresh(): Promise<RefreshOutcome> {
  const stored = getStoredSession()

  if (!stored || stored.refreshExpiresAt <= Date.now()) {
    setStoredSession(null)
    return { status: 'signedOut' }
  }

  try {
    // Unauthenticated on purpose: the access token this renews is usually the
    // expired one, and sending it would only invite a 401.
    const response = await apiClient.post<AuthSessionResponse>(
      endpoints.auth.refresh,
      { refreshToken: stored.refreshToken } satisfies RefreshRequest,
      { skipAuth: true },
    )

    const session = toSession(response)
    setStoredSession(session)
    return { status: 'renewed', session }
  } catch (error) {
    // Only a rejected token means the session is actually gone. A network blip,
    // a timeout, a 429 or a 5xx says nothing about whether the refresh token is
    // still valid — discarding it there would sign the user out because their
    // wifi dropped for a moment, and the stored token would still have been
    // good.
    if (isSessionRejected(error)) {
      setStoredSession(null)
      reportError(error, { scope: 'auth.refresh' })
      return { status: 'signedOut' }
    }

    reportError(error, { scope: 'auth.refresh' })
    return { status: 'unknown', error }
  }
}

/**
 * True when the server actively refused the refresh token, as opposed to the
 * request never getting a verdict.
 *
 * `rateLimited` is pointedly NOT here. A 429 on this endpoint (ten calls per
 * minute) means the server was busy, not that the token is bad — reading it as
 * a refusal is what used to sign a user out for reloading a page too often.
 */
function isSessionRejected(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  return error.kind === 'unauthorized' || error.kind === 'forbidden'
}

/**
 * Lets the API client renew an expired access token and replay the request. Set
 * at module load rather than in a React effect so the client can refresh before
 * any component has mounted.
 */
setTokenRefresher(async () => (await refreshSession()).status === 'renewed')

/* ---------------------------------------------------------------- passwords -- */
/**
 * Requests a password-reset email. Unauthenticated, so it skips the auth header.
 * The response is intentionally not branched on: the API answers the same way
 * (202) whether or not the address exists, so it never leaks who has an account.
 */
export function requestPasswordReset(payload: { email: string }): Promise<void> {
  return apiClient.post<void>(endpoints.auth.forgotPassword, payload, { skipAuth: true })
}

/**
 * Sets a new password using the token from the reset email's link
 * (`/reset-password?token=…`). Unauthenticated. An invalid or expired token
 * rejects with `ApiError` (code `INVALID_RESET_TOKEN`) whose message is safe to
 * show as-is.
 */
export function resetPassword(payload: {
  token: string
  password: string
  confirmPassword: string
}): Promise<void> {
  return apiClient.post<void>(endpoints.auth.resetPassword, payload, { skipAuth: true })
}

/**
 * Pre-flight check for the token in a reset link, so the screen can show the
 * expired notice instead of a form the user would fill in for nothing.
 * Unauthenticated.
 *
 * Resolves `false` only when the server actually rejects the token. A network
 * failure, a 429 or a 5xx rejects instead — the caller must not treat "no
 * verdict" as "expired" and dead-end a link that is still good, and equally
 * must not treat it as "valid" and show a form that cannot work.
 *
 * The response body is not part of the documented contract (the request is just
 * `{ token }`), so both plausible shapes are accepted: an empty/204 body means
 * usable, and an explicit `valid: false` means it is not.
 */
export async function validateResetToken(payload: { token: string }): Promise<boolean> {
  try {
    const response = await apiClient.post<{ valid?: boolean } | null>(
      endpoints.auth.validateResetToken,
      payload,
      { skipAuth: true },
    )
    return response?.valid !== false
  } catch (error) {
    if (isTokenRejected(error)) return false
    throw error
  }
}

/**
 * True when the server actively refused the token, as opposed to the request
 * never getting a verdict. Mirrors `isSessionRejected` above, widened for the
 * codes a one-time token can come back with.
 *
 * `rateLimited`, `server`, `network` and `timeout` are deliberately absent: the
 * server never looked at the token in those cases, so there is no verdict to
 * report and the error propagates for the caller to offer a retry.
 */
function isTokenRejected(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  return (
    error.kind === 'validation' ||
    error.kind === 'notFound' ||
    error.kind === 'unauthorized' ||
    error.kind === 'forbidden'
  )
}

/** NOT IMPLEMENTED on the backend yet — see `src/screens/auth/agent.md` §23. */
export function changePassword(payload: {
  currentPassword: string
  newPassword: string
}): Promise<void> {
  return apiClient.post<void>(endpoints.auth.changePassword, payload)
}
